import { create } from 'zustand'
import type { LocaleCode } from '@/i18n/locales'
import { clearAll, load, save, savedAt } from '@/lib/storage'
import type { StoreDefinition } from '@/lib/storage'
import type { AgeBand, Gender, Profile, Slot, SlotId, SlotSource } from '@/profile/types'
import { PROFILE_VERSION, emptyProfile, makeSlot } from '@/profile/types'
// Static, not lazy: "delete everything" has to be synchronous, or the screen
// re-renders from stores that have not been wiped yet. Neither of these
// imports reaches back to this file, so there is no cycle.
import { useDiagnosticsStore } from './diagnosticsStore'
import { useSettingsStore } from './settingsStore'

/**
 * What Namu remembers about the person.
 *
 * Two rules run through this file. Nothing is written before consent is
 * given, and everything written can be seen and undone from one screen. Both
 * are promises the assistant makes out loud during onboarding, so they are
 * enforced here rather than left to each caller to remember.
 */

const DEFINITION: StoreDefinition<Profile> = {
  key: 'profile',
  version: PROFILE_VERSION,
  fallback: emptyProfile,
  validate: (data): data is Profile => {
    if (typeof data !== 'object' || data === null) return false
    const value = data as Partial<Profile>
    // A slot is either absent or a well-formed slot. Anything else means the
    // stored blob was edited or written by something that is not this app.
    const slotOk = (slot: unknown) =>
      slot === null ||
      slot === undefined ||
      (typeof slot === 'object' && slot !== null && 'value' in slot && 'capturedAt' in slot)
    return (
      slotOk(value.name) &&
      slotOk(value.region) &&
      slotOk(value.consent) &&
      slotOk(value.interests) &&
      (value.completedAt === null || typeof value.completedAt === 'number')
    )
  },
}

interface ProfileState {
  profile: Profile
  /** When the profile was last written, for the profile screen. */
  savedAt: number | null

  /** Replaces the whole profile, e.g. when onboarding finishes. */
  commit(profile: Profile): void
  setName(value: string, source: SlotSource, confidence?: number): void
  setRegion(value: string, source: SlotSource, confidence?: number): void
  setAgeBand(value: AgeBand, source: SlotSource, confidence?: number): void
  setGender(value: Gender, source: SlotSource, confidence?: number): void
  setInterests(value: string[], source: SlotSource, confidence?: number): void
  setLanguage(value: LocaleCode): void
  setConsent(value: boolean, source: SlotSource): void
  /** Removes one answer without touching the rest. */
  clearSlot(slot: SlotId): void
  /** Forgets everything, including settings and diagnostics. */
  forgetEverything(): void
  hasConsent(): boolean
  isOnboarded(): boolean
  displayName(): string | null
}

export const useProfileStore = create<ProfileState>()((setState, getState) => {
  /**
   * Drops the verbatim transcript before writing.
   *
   * `raw` is the whole utterance an answer came from, and the whole utterance
   * can carry far more than the answer: an age given as "I'm 34, I've been on
   * medication since my diagnosis" would sit in storage forever. The consent
   * screen enumerates what is kept as name, region, age range and interests,
   * and promises everything kept can be reviewed. Nothing renders `raw`, so it
   * was neither reviewable nor disclosed. It stays in memory for the read-back
   * that actually uses it, and never reaches the disk.
   */
  const withoutTranscripts = (profile: Profile): Profile => {
    const strip = <T,>(slot: Slot<T> | null): Slot<T> | null => {
      if (!slot) return null
      const { raw: _raw, ...rest } = slot
      return rest
    }
    return {
      ...profile,
      name: strip(profile.name),
      region: strip(profile.region),
      ageBand: strip(profile.ageBand),
      gender: strip(profile.gender),
      interests: strip(profile.interests),
      consent: strip(profile.consent),
    }
  }

  const persist = (profile: Profile) => {
    // The consent gate, enforced in one place. Without consent the only thing
    // worth keeping is the fact that onboarding ran, so the person is not
    // asked the same questions every time they open the app.
    if (profile.consent?.value !== true) {
      const minimal: Profile = { ...emptyProfile(), completedAt: profile.completedAt }
      save(DEFINITION, minimal)
      setState({ savedAt: savedAt(DEFINITION.key) })
      return
    }
    save(DEFINITION, withoutTranscripts(profile))
    setState({ savedAt: savedAt(DEFINITION.key) })
  }

  const update = (mutate: (draft: Profile) => void) => {
    const profile = { ...getState().profile }
    mutate(profile)
    setState({ profile })
    persist(profile)
  }

  /**
   * Writes one slot. The caller supplies the assignment rather than a key
   * name, which keeps the whole thing typed: there is no cast anywhere, and a
   * slot written to the wrong field is a compile error rather than a profile
   * that quietly remembers someone's region as their name.
   */
  const setSlot = <T,>(
    assign: (draft: Profile, slot: Slot<T>) => void,
    value: T,
    source: SlotSource,
    confidence: number,
  ) => {
    update((draft) => assign(draft, makeSlot(value, source, confidence)))
  }

  return {
    profile: load(DEFINITION),
    savedAt: savedAt(DEFINITION.key),

    commit(profile) {
      setState({ profile })
      persist(profile)
    },

    setName(value, source, confidence = 1) {
      setSlot((draft, slot) => (draft.name = slot), value, source, confidence)
    },
    setRegion(value, source, confidence = 1) {
      setSlot((draft, slot) => (draft.region = slot), value, source, confidence)
    },
    setAgeBand(value, source, confidence = 1) {
      setSlot((draft, slot) => (draft.ageBand = slot), value, source, confidence)
    },
    setGender(value, source, confidence = 1) {
      setSlot((draft, slot) => (draft.gender = slot), value, source, confidence)
    },
    setInterests(value, source, confidence = 1) {
      setSlot((draft, slot) => (draft.interests = slot), value, source, confidence)
    },

    setLanguage(value) {
      update((draft) => {
        draft.language = makeSlot(value, 'chosen', 1)
      })
    },

    setConsent(value, source) {
      update((draft) => {
        draft.consent = makeSlot(value, source, 1)
      })
    },

    clearSlot(slot) {
      update((draft) => {
        switch (slot) {
          case 'language':
            draft.language = null
            break
          case 'consent':
            draft.consent = null
            break
          case 'name':
            draft.name = null
            break
          case 'region':
            draft.region = null
            break
          case 'ageBand':
            draft.ageBand = null
            break
          case 'gender':
            draft.gender = null
            break
          case 'interests':
            draft.interests = null
            break
        }
      })
    },

    /**
     * Forgets everything, for real.
     *
     * Clearing storage is not enough on its own. The settings and diagnostics
     * stores still hold their pre-deletion state in memory, and each one
     * re-serialises the whole of its own state on the next write, so a single
     * recorded turn or settings toggle wrote the entire old history back. The
     * dialog says "this cannot be undone"; it has to be true.
     */
    forgetEverything() {
      setState({ profile: emptyProfile(), savedAt: null })
      useDiagnosticsStore.setState({
        records: [],
        typedFallbacks: 0,
        interruptions: 0,
        rejectedTransitions: 0,
      })
      useSettingsStore.getState().reset()
      // Last, so nothing the resets above wrote survives the wipe.
      clearAll()
    },

    hasConsent() {
      return getState().profile.consent?.value === true
    },

    isOnboarded() {
      return getState().profile.completedAt !== null
    },

    displayName() {
      return getState().profile.name?.value ?? null
    },
  }
})
